// ==============================
// 파일명 : src/pages/UsersPage.jsx
// 역할 : 관리자용 사용자 목록을 조회·검색·페이지네이션하고, 보유팩 및 기기관리,
//         Firebase UID 동기화를 포함한 계정 CRUD 작업을 통합 제어하는 화면 컴포넌트
// 수정일 : 2025-10-14
// ==============================

import { useEffect, useMemo, useRef, useState } from "react";
import LoadingBar from "../components/LoadingBar";
import PaginationFooter from "../components/common/PaginationFooter";
import UserFormModal from "../components/users/UserFormModal";
import UserRemovalModal from "../components/users/UserRemovalModal"; // [수정] 삭제 확인 모달 컴포넌트 추가
import DeviceManager from "../components/videos/DeviceManager";
import UserRowActions from "../components/users/UserRowActions";
import UserStatusBadge from "../components/users/UserStatusBadge";
import useUserAccountSyncManager from "../components/users/UserAccountSyncManager"; // [수정] Auth-동기화 훅 추가
import {
  getUsers,
  getWordPacks,
  markUserWithdrawn,
  getPurchasesByUser,
  updateUserStatus,
} from "../lib/firestore";
import { STRINGS } from "../constants/strings";
import { USER_FIELD_AUTH_UID } from "../constants/users/fields"; // [수정] UID 필드 이름 상수
import {
  displayNameOf,
  extractOwnedPackIds,
  makePackLabel,
  normalizePackIds,
  normalizeAuthUid,
} from "../utils/users"; // [수정] 보유팩 및 UID 정규화 유틸 사용
import { generateUserAuthUid } from "../utils/uid"; // [수정] UID 자동 생성 유틸
import { formatDateOnly } from "../utils/date";

export default function UsersPage() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [packs, setPacks] = useState([]);

  const { createUserAccount, updateUserAccount, deleteUserAccount } =
    useUserAccountSyncManager(); // [수정] Firebase Auth-동기화 함수 모음

  const [q, setQ] = useState("");
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [editingUser, setEditingUser] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const addBtnRef = useRef(null);

  // [2025-09-27 PATCH] 선택된 유저(하단 기기관리 연동)
  const [selectedUser, setSelectedUser] = useState(null);
  const deviceSectionRef = useRef(null);

  // [수정] 삭제 확인 모달 제어용 상태
  const [removalTarget, setRemovalTarget] = useState(null);
  const [removalPurchases, setRemovalPurchases] = useState([]);
  const [removalLoading, setRemovalLoading] = useState(false);
  const [showRemovalModal, setShowRemovalModal] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    const [userList, packList] = await Promise.all([getUsers(), getWordPacks()]);
    setUsers(userList);
    setPacks(packList);
    setLoading(false);
    setSelectedUser((prev) => (prev ? userList.find((u) => u.id === prev.id) || null : null));
  };
  useEffect(() => { fetchAll(); }, []);

  const packMap = useMemo(() => new Map(packs.map((p) => [p.id, p])), [packs]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return users;
    return users.filter((u) => {
      const email = (u.email || "").toLowerCase();
      const name = displayNameOf(u).toLowerCase();
      return email.includes(qq) || name.includes(qq);
    });
  }, [q, users]);

  const activeFiltered = useMemo(
    () =>
      filtered.filter((u) => (u.accountStatus?.state || "active") !== "withdrawn"),
    [filtered]
  );

  const withdrawnList = useMemo(
    () =>
      users
        .filter((u) => u.accountStatus?.state === "withdrawn")
        .sort((a, b) => (b.accountStatus?.updatedAt || 0) - (a.accountStatus?.updatedAt || 0)),
    [users]
  );

  const total = activeFiltered.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(Math.max(1, page), lastPage);
  const slice = activeFiltered.slice((safePage - 1) * perPage, safePage * perPage);

  const openAdd = () => {
    setFormMode("add");
    setEditingUser(null);
    setAnchorRect(addBtnRef.current?.getBoundingClientRect() ?? null);
    setShowForm(true);
  };
  const openEdit = (u, anchor) => {
    setFormMode("edit");
    setEditingUser(u);
    setAnchorRect(anchor?.getBoundingClientRect?.() ?? null);
    setShowForm(true);
  };
  const closeForm = () => setShowForm(false);

  const handleSave = async (mode, data, idWhenEdit) => {
    try {
      console.log("🔥 UsersPage handleSave 받은 data:", data);
      console.log("🔥 data.ownedPacks:", data.ownedPacks);
      const normalizedOwned = normalizePackIds(data.ownedPacks); // [수정-2025-10-14] 보유팩 ID 정규화
      console.log("🔥 normalizedOwned:", normalizedOwned);
      const normalizedAuthUid = normalizeAuthUid(
        data[USER_FIELD_AUTH_UID],
        idWhenEdit
      ); // [수정-2025-10-14] UID 정규화 및 기존 UID 보존
      const ensuredAuthUid =
        mode === "add"
          ? normalizedAuthUid || generateUserAuthUid()
          : normalizeAuthUid(normalizedAuthUid, idWhenEdit); // [수정-2025-10-14] 상황별 UID 보존 및 생성
      const trimmedPassword = data.password ? data.password.trim() : ""; // [수정-2025-10-14] 비밀번호 공백 제거
      const now = Date.now(); // [수정-2025-10-14] 저장 시점 기록

      const authPayload = {
        email: data.email,
        password: trimmedPassword,
        displayName: data.displayName,
        }; // [수정-2025-10-14] Auth 동기화용 데이터

      if (mode === "add") {
        const firestorePayload = {
          displayName: data.displayName,
          email: data.email,
          createdAt: now,
          updatedAt: now,
          [USER_FIELD_AUTH_UID]: ensuredAuthUid,
          accountStatus: {
            state: "active",
            note: "",
            scheduledDeletionAt: null,
          },
        }; // [수정-2025-10-14] 신규 사용자 Firestore 데이터
        await createUserAccount({
          firestore: firestorePayload,
          auth: authPayload,
          ownedPacks: normalizedOwned,
        }); // [수정-2025-10-14] Auth + Firestore 동시 생성
      } else if (idWhenEdit) {
        const firestorePayload = {
          displayName: data.displayName,
          email: data.email,
          updatedAt: now,
          [USER_FIELD_AUTH_UID]: ensuredAuthUid,
        }; // [수정-2025-10-14] 기존 사용자 Firestore 데이터
        await updateUserAccount(idWhenEdit, {
          firestore: firestorePayload,
          auth: authPayload,
          ownedPacks: normalizedOwned,
        }); // [수정-2025-10-14] Auth + Firestore 동시 수정
      }

      await fetchAll(); // [수정-2025-10-14] 최신 사용자 목록 갱신
      setShowForm(false); // [수정-2025-10-14] 저장 성공 시 모달 닫기
    } catch (error) {
      console.error("log :", "[UsersPage]", "사용자 저장 실패", error); // [수정-2025-10-14] 저장 실패 로그

    let displayMessage =
        error?.message || STRINGS.users.alerts.operationFailed; // [수정-2025-10-14] 기본 노출 메시지 결정

      if (error?.serverMessage && error.serverMessage !== error.message) {
        displayMessage += `\n\n상세 정보: ${error.serverMessage}`; // [수정-2025-10-14] 서버 상세 메시지 추가
      }

      if (process.env.NODE_ENV === "development" && error?.code) {
        displayMessage += `\n\n에러 코드: ${error.code}`; // [수정-2025-10-14] 개발 환경용 코드 노출
      }

      alert(displayMessage); // [수정-2025-10-14] 사용자에게 에러 안내
    }
  };

  // [수정] 탈퇴 처리를 담당하는 핸들러
  const handleWithdraw = async (user) => {
    if (!user) return;
    if (!window.confirm(STRINGS.users.confirmations.withdraw)) return;
    try {
      await markUserWithdrawn(user.id);
      await fetchAll();
      alert(STRINGS.users.alerts.withdrawSuccess);
      setSelectedUser((prev) => (prev && prev.id === user.id ? null : prev));
    } catch (error) {
      console.error(error);
      alert(STRINGS.users.alerts.operationFailed);
    }
  };

  const handleRestoreWithdrawn = async (user) => {
    if (!user) return;
    const name = displayNameOf(user);
    if (!window.confirm(STRINGS.users.withdrawn.confirmations.restore(name))) return;
    try {
      await updateUserStatus(user.id, {
        state: "active",
        updatedAt: Date.now(),
        note: "관리자에 의해 재가입 처리됨",
        scheduledDeletionAt: null,
      });
      await fetchAll();
      alert(STRINGS.users.alerts.restoreSuccess);
      setSelectedUser((prev) => (prev && prev.id === user.id ? null : prev));
    } catch (error) {
      console.error(error);
      alert(STRINGS.users.alerts.operationFailed);
    }
  };

  // [수정] 삭제 모달을 열고 구매 내역을 조회한다.
  const openRemovalModal = async (user) => {
    if (!user) return;
    setRemovalTarget(user);
    setShowRemovalModal(true);
    setRemovalLoading(true);
    setRemovalPurchases([]);
    try {
      const purchases = await getPurchasesByUser(user.id);
      setRemovalPurchases(purchases);
    } catch (error) {
      console.error(error);
      alert(STRINGS.users.alerts.operationFailed);
    } finally {
      setRemovalLoading(false);
    }
  };

  const closeRemovalModal = () => {
    setShowRemovalModal(false);
    setRemovalTarget(null);
    setRemovalPurchases([]);
    setRemovalLoading(false);
  };

  const confirmRemoval = async (user) => {
    if (!user) return;
    setRemovalLoading(true);
    try {
      await deleteUserAccount(user, { reason: "admin-delete" }); // [수정] Auth + Firestore 동시 삭제
      await fetchAll();
      alert(STRINGS.users.alerts.deleteSuccess);
      setSelectedUser((prev) => (prev && prev.id === user.id ? null : prev));
      closeRemovalModal();
    } catch (error) {
      console.error(error);
      alert(STRINGS.users.alerts.operationFailed);
      setRemovalLoading(false);
    }
  };

  // [2025-09-27 PATCH] 행 클릭 = 선택 + 기기관리 섹션으로 스크롤
  const selectUser = (u) => {
    setSelectedUser(u || null);
    setTimeout(() => {
      deviceSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  function ownedPackText(u) {
    const ids = extractOwnedPackIds(u) || [];
    if (ids.length === 0) return "-";
    const labels = ids
      .map((id) => makePackLabel(packMap.get(id)))
      .filter(Boolean);
    if (labels.length <= 3) return labels.join(", ");
    return `${labels.slice(0, 3).join(", ")} 외 ${labels.length - 3}개`;
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-2xl font-bold">{STRINGS.users.title}</h1>
        <div className="flex items-center gap-2">
          <input
            className="border rounded px-3 py-2 w-64"
            placeholder={STRINGS.users.searchPlaceholder}
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
          />
          <select
            className="border rounded px-2 py-2"
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
          >
            {STRINGS.users.perPageOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            ref={addBtnRef}
            className="px-3 py-2 rounded bg-blue-600 text-white"
            onClick={openAdd}
          >
            {STRINGS.users.addButton}
          </button>
        </div>
      </div>

      <LoadingBar show={loading} />

      <div className="overflow-x-auto border rounded bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr className="text-left">
             <th className="px-4 py-2">{STRINGS.users.table.name}</th>
              <th className="px-4 py-2">{STRINGS.users.table.email}</th>
              <th className="px-4 py-2">{STRINGS.users.table.packs}</th>
              <th className="px-4 py-2">{STRINGS.users.table.status}</th>
              <th className="px-4 py-2 text-center">{STRINGS.users.table.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {slice.map((u) => {
              const isSel = selectedUser && selectedUser.id === u.id;
              return (
                <tr
                  key={u.id}
                  className={`cursor-pointer ${isSel ? "bg-blue-50" : "hover:bg-gray-50"}`}
                  onClick={() => selectUser(u)}
                >
                  <td className="px-4 py-2">{displayNameOf(u)}</td>
                  <td className="px-4 py-2">{u.email || "-"}</td>
                  <td className="px-4 py-2">{ownedPackText(u)}</td>
                  <td className="px-4 py-2"><UserStatusBadge status={u.accountStatus} /></td>
                  <td className="px-4 py-2">
                    <UserRowActions
                      onEdit={(e) => { e.stopPropagation(); openEdit(u, e.currentTarget); }}
                      onWithdraw={(e) => { e.stopPropagation(); handleWithdraw(u); }}
                      onDelete={(e) => { e.stopPropagation(); openRemovalModal(u); }}
                    />
                  </td>
                </tr>
              );
            })}
              {slice.length === 0 && !loading && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-500" colSpan={5}>
                    {STRINGS.common.messages.noResults}
                  </td>
                </tr>
              )}
          </tbody>
        </table>
      </div>

      <PaginationFooter
        page={page}
        perPage={perPage}
        total={total}
        onPageChange={setPage}
        className="px-1 py-3"
      />

      {/* 모달 */}
      {showForm && (
        <UserFormModal
          mode={formMode}
          initialUser={editingUser}
          packs={packs}
          anchorRect={anchorRect}
          onClose={closeForm}
          onSave={handleSave}
          yOffset={8}
        />
      )}

{showRemovalModal && (
        <UserRemovalModal
          user={removalTarget}
          packs={packs}
          purchases={removalPurchases}
          loading={removalLoading}
          onClose={closeRemovalModal}
          onConfirm={confirmRemoval}
        />
      )}

      <div className="mt-10 space-y-3">
        <h2 className="text-xl font-bold">{STRINGS.users.withdrawn.title}</h2>
        <div className="overflow-x-auto border rounded bg-white">
          {withdrawnList.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500">{STRINGS.users.withdrawn.empty}</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr className="text-left">
                  <th className="px-4 py-2">{STRINGS.users.withdrawn.columns.name}</th>
                  <th className="px-4 py-2">{STRINGS.users.withdrawn.columns.email}</th>
                  <th className="px-4 py-2">{STRINGS.users.withdrawn.columns.withdrawnAt}</th>
                  <th className="px-4 py-2">{STRINGS.users.withdrawn.columns.scheduledDeletionAt}</th>
                  <th className="px-4 py-2 text-right">{STRINGS.users.withdrawn.columns.actions}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {withdrawnList.map((user) => (
                  <tr key={user.id}>
                    <td className="px-4 py-2">{displayNameOf(user)}</td>
                    <td className="px-4 py-2">{user.email || "-"}</td>
                    <td className="px-4 py-2">{formatDateOnly(user.accountStatus?.updatedAt)}</td>
                    <td className="px-4 py-2">{formatDateOnly(user.accountStatus?.scheduledDeletionAt)}</td>
                     <td className="px-4 py-2">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                          onClick={() => handleRestoreWithdrawn(user)}
                        >
                          {STRINGS.users.withdrawn.actions.restore}
                        </button>
                        <button
                          type="button"
                          className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                          onClick={() => openRemovalModal(user)}
                        >
                          {STRINGS.users.withdrawn.actions.delete}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 하단 DeviceManager: 선택된 유저만으로 자동 표시 */}
      <div ref={deviceSectionRef} className="mt-8 space-y-3">
        <h2 className="text-xl font-bold">{STRINGS.users.deviceManagerTitle}</h2>
        <DeviceManager selectedUser={selectedUser} />
      </div>
    </div>
  );
}
